import React, { useEffect, useState, useRef } from "react";

/**
 * Notifications Twilio — WhatsApp + SMS
 * -------------------------------------
 * - Composer une campagne (rappel cotisation, annonce festival, urgence AVC…)
 * - Choisir le canal : WhatsApp, SMS ou les deux
 * - Coller la liste des destinataires (CSV : Nom ; Téléphone ; Email)
 * - Suivre l'historique des envois (statut de livraison Twilio)
 */
export default function TwilioNotificationsAdminPanel({ API, pinInput }) {
  const [config, setConfig] = useState(null);
  const [channel, setChannel] = useState("whatsapp"); // whatsapp | sms | both
  const [template, setTemplate] = useState(
    "Bonjour {prenom} 🌿\n\n" +
    "Rappel SAFUAYE-Togo : n'oubliez pas votre cotisation annuelle 3 000 FCFA " +
    "(exercice 2027) via Yas 91 14 25 19.\n\n" +
    "Merci pour votre soutien continu. Akpé kaka. 🙏\n— Le Bureau Exécutif"
  );
  const [mediaUrl, setMediaUrl] = useState("");
  const [label, setLabel] = useState("rappel-cotisation");
  const [rawList, setRawList] = useState(
    "# Nom ; Téléphone ; Email\n" +
    "APEDO Ablavi ; +228 91 14 25 19 ; ablavi@example.com\n" +
    "DOGLO Komi ; +228 90 12 34 56 ; doglo@example.com"
  );
  const [busy, setBusy] = useState(false);
  const [report, setReport] = useState(null);
  const [msg, setMsg] = useState("");
  const [msgType, setMsgType] = useState("info");
  const [history, setHistory] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const fileInputRef = useRef(null);

  const _parse = (text) =>
    text.split("\n").map((l) => l.trim())
      .filter((l) => l && !l.startsWith("#"))
      .map((line) => {
        const parts = line.split(/\s*[;,\t]\s*/);
        return { nom: parts[0] || "", telephone: parts[1] || "", email: parts[2] || "" };
      })
      .filter((it) => it.nom);

  const recipients = _parse(rawList);
  const withPhone = recipients.filter(
    (r) => r.telephone && r.telephone.replace(/\D/g, "").length >= 8
  ).length;

  useEffect(() => {
    if (!pinInput) return;
    fetch(`${API}/twilio/config-check?pin=${encodeURIComponent(pinInput)}`)
      .then((r) => r.ok ? r.json() : null)
      .then((c) => setConfig(c))
      .catch(() => setConfig(null));
  }, [API, pinInput]);

  const handleImportCsv = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const text = await file.text();
      const lines = text.split(/\r?\n/).filter((l) => l.trim());
      if (lines.length === 0) {
        setMsg("Fichier CSV vide."); setMsgType("error"); return;
      }
      const sep = lines[0].includes(";") ? ";" : ",";
      const firstLower = lines[0].toLowerCase();
      const isHeader = firstLower.includes("nom") || firstLower.includes("tél") ||
                       firstLower.includes("phone") || firstLower.includes("email");
      const dataLines = isHeader ? lines.slice(1) : lines;
      const normalised = dataLines.map((line) => {
        const parts = line.split(sep).map((p) => p.trim().replace(/^["']|["']$/g, ""));
        return parts.slice(0, 3).join(" ; ");
      });
      const header = "# Importé depuis " + file.name + " (" + normalised.length + " lignes)";
      setRawList([header, ...normalised].join("\n"));
      setMsg(`✅ ${normalised.length} destinataires importés.`); setMsgType("success");
    } catch (err) {
      setMsg(`❌ Erreur : ${err.message}`); setMsgType("error");
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const sendCampaign = async () => {
    if (!pinInput) { setMsg("PIN administrateur manquant."); setMsgType("error"); return; }
    if (recipients.length === 0) { setMsg("Aucun destinataire valide."); setMsgType("error"); return; }
    if (!template.trim()) { setMsg("Le message est vide."); setMsgType("error"); return; }
    if (!window.confirm(
      `Envoyer ce message via ${channel === "both" ? "WhatsApp + SMS" : channel.toUpperCase()} ` +
      `à ${withPhone} destinataire(s) avec numéro valide ?\n\n` +
      `(${recipients.length - withPhone} ligne(s) sans numéro seront ignorées côté Twilio)`
    )) return;
    setBusy(true); setReport(null);
    setMsg(`📲 Envoi en cours via ${channel}…`); setMsgType("info");
    try {
      const res = await fetch(`${API}/twilio/send-notifications`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          pin: pinInput,
          recipients,
          channel,
          message_template: template,
          media_url: mediaUrl.trim() || null,
          campaign_label: label.trim() || "notification",
        }),
      });
      if (!res.ok) throw new Error(await res.text() || `HTTP ${res.status}`);
      const data = await res.json();
      setReport(data);
      setMsg(`✅ Campagne envoyée — ${data.sent}/${data.total_messages} messages transmis à Twilio.`);
      setMsgType(data.failed > 0 ? "info" : "success");
    } catch (e) {
      setMsg(`❌ Erreur : ${e.message}`); setMsgType("error");
    } finally { setBusy(false); }
  };

  const loadHistory = async () => {
    if (!pinInput) return;
    setBusy(true);
    try {
      const res = await fetch(`${API}/twilio/history?pin=${encodeURIComponent(pinInput)}&limit=100`);
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setHistory(data);
      setShowHistory(true);
    } catch (e) {
      setMsg(`❌ Erreur historique : ${e.message}`); setMsgType("error");
    } finally { setBusy(false); }
  };

  const statusEmoji = (item) => {
    if (!item.sent) return "❌";
    switch ((item.status || "").toLowerCase()) {
      case "delivered": return "✅";
      case "read": return "👁️";
      case "sent": return "📤";
      case "queued": return "⏳";
      case "failed":
      case "undelivered": return "❌";
      default: return "📤";
    }
  };

  return (
    <div data-testid="twilio-notifications-panel">
      <h3 className="font-heading text-xl font-bold text-gray-900 mb-2">
        📲 Notifications Twilio — WhatsApp &amp; SMS
      </h3>
      <p className="text-sm text-gray-600 mb-4">
        Envoyer des messages automatiques (rappels cotisation, annonces festival, alertes AVC)
        via l&apos;API officielle Twilio. Chaque envoi est tracé dans MongoDB avec son statut
        de livraison.
      </p>

      {/* Statut config Twilio */}
      {config && (
        <div className={`mb-4 p-3 rounded-lg text-sm border-2 ${
          config.configured
            ? "bg-green-50 border-green-200 text-green-800"
            : "bg-amber-50 border-amber-300 text-amber-900"
        }`} data-testid="twilio-config-status">
          {config.configured ? (
            <>
              ✅ <b>Twilio configuré</b> — SID {config.account_sid_prefix}
              {config.whatsapp_from && <> · WhatsApp : <code className="text-xs">{config.whatsapp_from}</code></>}
              {config.sms_from && <> · SMS : <code className="text-xs">{config.sms_from}</code></>}
            </>
          ) : (
            <>
              ⚠️ <b>Twilio non configuré.</b> Ajouter dans <code>backend/.env</code> :
              <code className="ml-1 text-xs bg-white px-1 rounded">
                TWILIO_ACCOUNT_SID, TWILIO_AUTH_TOKEN, TWILIO_WHATSAPP_FROM, TWILIO_SMS_FROM
              </code>
              <br/>Créer un compte sur <a href="https://www.twilio.com/try-twilio" target="_blank" rel="noreferrer" className="underline">twilio.com/try-twilio</a>{" "}
              → activer le Sandbox WhatsApp (<code>whatsapp:+14155238886</code>) et récupérer un numéro d&apos;expéditeur SMS.
            </>
          )}
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Colonne gauche — composer */}
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-2">Canal</label>
            <div className="flex gap-2" data-testid="twilio-channel-selector">
              {[
                { v: "whatsapp", label: "📱 WhatsApp", color: "green" },
                { v: "sms", label: "💬 SMS", color: "blue" },
                { v: "both", label: "📤 Les deux", color: "purple" },
              ].map((opt) => (
                <button
                  key={opt.v}
                  onClick={() => setChannel(opt.v)}
                  data-testid={`twilio-channel-${opt.v}`}
                  className={`flex-1 px-3 py-2 rounded-lg font-semibold text-sm border-2 transition ${
                    channel === opt.v
                      ? `bg-${opt.color}-600 border-${opt.color}-700 text-white shadow`
                      : "bg-white border-gray-300 text-gray-600 hover:border-gray-400"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-bold text-gray-700 mb-2" htmlFor="twilio-label">
              Libellé campagne
            </label>
            <input
              id="twilio-label"
              type="text"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              className="w-full border-2 border-gray-300 rounded-lg px-3 py-2 text-sm focus:border-amber-600 focus:outline-none"
              placeholder="ex : rappel-cotisation-2027"
              data-testid="twilio-campaign-label"
            />
          </div>

          <div>
            <label className="block text-sm font-bold text-gray-700 mb-2" htmlFor="twilio-template">
              Message (variables : <code>{"{prenom}"}</code>, <code>{"{nom}"}</code>)
            </label>
            <textarea
              id="twilio-template"
              value={template}
              onChange={(e) => setTemplate(e.target.value)}
              rows={6}
              className="w-full border-2 border-gray-300 rounded-lg px-3 py-2 text-sm focus:border-amber-600 focus:outline-none"
              data-testid="twilio-message-template"
            />
            <p className="text-xs text-gray-500 mt-1">
              Longueur : {template.length} car. {channel === "sms" && template.length > 160 && (
                <span className="text-amber-700">(SMS &gt; 160 = plusieurs messages facturés)</span>
              )}
            </p>
          </div>

          {channel !== "sms" && (
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2" htmlFor="twilio-media">
                Pièce jointe WhatsApp (URL publique — PDF ou image, optionnel)
              </label>
              <input
                id="twilio-media"
                type="url"
                value={mediaUrl}
                onChange={(e) => setMediaUrl(e.target.value)}
                placeholder="https://safuaye.org/docs/mon-fichier.pdf"
                className="w-full border-2 border-gray-300 rounded-lg px-3 py-2 text-sm font-mono focus:border-amber-600 focus:outline-none"
                data-testid="twilio-media-url"
              />
            </div>
          )}

          <div className="flex gap-2 pt-2">
            <button
              onClick={sendCampaign}
              disabled={busy || recipients.length === 0}
              data-testid="twilio-send-campaign"
              className="flex-1 px-6 py-3 bg-emerald-600 hover:bg-emerald-700 disabled:bg-gray-300 text-white font-bold rounded-lg shadow"
            >
              {busy ? "⏳ Envoi…" : `🚀 Envoyer à ${withPhone} destinataire(s)`}
            </button>
            <button
              onClick={loadHistory}
              disabled={busy}
              data-testid="twilio-open-history"
              className="px-4 py-3 bg-slate-700 hover:bg-slate-800 text-white text-sm font-semibold rounded-lg"
            >
              📊 Historique
            </button>
          </div>

          {msg && (
            <div
              data-testid="twilio-status"
              className={`p-3 rounded-lg text-sm ${
                msgType === "success" ? "bg-green-50 text-green-800 border border-green-200"
                : msgType === "error" ? "bg-red-50 text-red-800 border border-red-200"
                : "bg-blue-50 text-blue-800 border border-blue-200"
              }`}
            >{msg}</div>
          )}
        </div>

        {/* Colonne droite — destinataires ou résultat/historique */}
        <div>
          {showHistory && history ? (
            <>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-bold text-gray-700">
                  Historique ({history.count}/{history.total} · ✅ {history.sent} · ❌ {history.failed})
                </label>
                <button
                  onClick={() => setShowHistory(false)}
                  className="text-xs text-blue-700 hover:underline"
                >← Retour composer</button>
              </div>
              <div className="border-2 border-slate-200 rounded-lg overflow-hidden">
                <div className="max-h-[26rem] overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-100 sticky top-0">
                      <tr>
                        <th className="px-2 py-1.5 text-left">Nom</th>
                        <th className="px-2 py-1.5 text-left">Canal</th>
                        <th className="px-2 py-1.5 text-left">Date</th>
                        <th className="px-2 py-1.5 text-center">Statut</th>
                      </tr>
                    </thead>
                    <tbody>
                      {history.items.length === 0 && (
                        <tr><td colSpan={4} className="px-3 py-6 text-center text-gray-400 italic">
                          Aucun envoi enregistré.
                        </td></tr>
                      )}
                      {history.items.map((it, i) => (
                        <tr key={it.id || i} className={i % 2 === 0 ? "bg-white" : "bg-slate-50"}>
                          <td className="px-2 py-1.5">
                            <div className="font-medium">{it.adherent_name}</div>
                            <div className="text-[10px] text-gray-500 font-mono">
                              {it.phone || "—"} · {it.module}
                            </div>
                          </td>
                          <td className="px-2 py-1.5">
                            {it.channel === "whatsapp" ? "📱 WA" : "💬 SMS"}
                          </td>
                          <td className="px-2 py-1.5 text-[10px] text-gray-600">
                            {new Date(it.created_at).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" })}
                          </td>
                          <td className="px-2 py-1.5 text-center">
                            <span className="text-lg" title={it.status || it.reason}>{statusEmoji(it)}</span>
                            <div className="text-[9px] text-gray-500 truncate max-w-[140px] mx-auto">
                              {it.status || it.reason}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              <p className="text-[11px] text-gray-500 mt-2">
                💡 Les statuts (delivered, read, failed) sont mis à jour automatiquement par le
                webhook Twilio (<code>/api/twilio/status-webhook</code>).
              </p>
            </>
          ) : report ? (
            <>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-bold text-gray-700">
                  Résultat ({report.sent}/{report.total_messages} envoyés)
                </label>
                <button
                  onClick={() => setReport(null)}
                  className="text-xs text-blue-700 hover:underline"
                >← Nouveau message</button>
              </div>
              <div className="border-2 border-emerald-200 rounded-lg overflow-hidden">
                <div className="max-h-[26rem] overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-emerald-50 sticky top-0">
                      <tr>
                        <th className="px-2 py-2 text-left">Nom</th>
                        <th className="px-2 py-2 text-left">Téléphone</th>
                        <th className="px-2 py-2 text-center">Statut</th>
                      </tr>
                    </thead>
                    <tbody>
                      {report.results.map((r, i) => (
                        <tr key={i} className="border-b border-gray-100">
                          <td className="px-2 py-1.5 font-medium">{r.nom}</td>
                          <td className="px-2 py-1.5 text-xs font-mono">{r.phone || "—"}</td>
                          <td className="px-2 py-1.5 text-center">
                            {r.channels.map((c, j) => (
                              <div key={j} className="text-[11px]">
                                {c.channel === "whatsapp" ? "📱" : "💬"}{" "}
                                {c.sent ? "✅" : "❌"}{" "}
                                <span className="text-gray-500">{c.status || c.reason}</span>
                              </div>
                            ))}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-bold text-gray-700">
                  Destinataires ({recipients.length} · {withPhone} avec numéro)
                </label>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv,text/csv,text/plain"
                  onChange={handleImportCsv}
                  className="hidden"
                  data-testid="twilio-csv-input"
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  data-testid="twilio-import-csv"
                  className="text-xs bg-amber-100 hover:bg-amber-200 border border-amber-400 text-amber-800 px-2 py-1 rounded font-semibold"
                >📥 Importer .csv</button>
              </div>
              <textarea
                value={rawList}
                onChange={(e) => setRawList(e.target.value)}
                rows={14}
                data-testid="twilio-recipients-textarea"
                className="w-full border-2 border-gray-300 rounded-lg px-3 py-2 font-mono text-xs focus:border-amber-600 focus:outline-none"
                placeholder="APEDO Ablavi ; +228 91 14 25 19 ; ablavi@example.com"
              />
              <p className="text-[11px] text-gray-500 mt-2">
                Format : <code>Nom ; Téléphone ; Email</code> — une ligne par personne.
                Les numéros à 8 chiffres sont automatiquement préfixés <code>+228</code> (Togo).
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
